import { useSyncExternalStore } from 'react';
import { Link } from 'react-router-dom';
import { freshnessSnapshot, subscribeFreshness } from '../session/freshness';
import type { DroppedBatch, QueueView } from '../vault/writeQueue';

/**
 * Off while there is no key — nothing is syncing, and a hopeful dot would be
 * a lie about a page that cannot read anything yet.
 *
 * A dot while nothing asks anything of you, its colour saying which quiet
 * state it is and its title saying it in words. Words only for a state that
 * asks something: an edit not yet sent, a write that failed, access to take
 * back, a refresh that cannot happen, a write about to overwrite another.
 * `unsaved` is one of them rather than folded into writing: edits wait a few
 * seconds before they go, and a dot that says "in sync" through that window
 * is claiming something untrue about the user's data.
 */
export function SyncDot({ state, stale, queue }: { state: string; stale: string | null; queue: QueueView }) {
  const freshness = useSyncExternalStore(subscribeFreshness, freshnessSnapshot);
  if (state === 'disconnected') return null;

  // What THIS tab owes comes before what it has not been told, because one is
  // the person's own work and the other is somebody else's.
  const [tone, label, asks, reconnect = false]: [string, string, boolean, boolean?] =
    queue.state === 'error'
      ? ['error', queue.error ?? 'not saved', true]
      : queue.state === 'unsaved'
        ? ['unsaved', 'unsaved', true]
        : queue.state === 'saving'
          ? ['saving', 'writing…', false]
          : state === 'loading'
            ? ['loading', 'reading…', false]
            : freshness.cutOff
              ? ['error', 'access withdrawn', true, true]
              : // A ready vault that could not refresh: what is on screen came
                // from the cache, and everything asked for after this will fail
                // the same way.
                stale
                ? ['error', 'cannot refresh', true]
                : freshness.conflicts.length > 0
                  ? ['behind', 'will overwrite', true]
                  : freshness.behind !== null
                    ? ['behind', 'changes elsewhere', false]
                    : ['synced', 'in sync', false];

  // Nothing this tab does will work again until the phone lets it back in, so
  // the one thing worth offering is the way to ask.
  if (reconnect) {
    return (
      <Link to="/connect" className={`sync t-metaMono sync-${tone}`} title="Connect this browser again">
        <span className="sync-dot" /> {label} · reconnect
      </Link>
    );
  }

  // Nothing was saved and it will not be sent again, so this one is worth
  // hearing about.
  if (queue.state === 'error' && queue.dropped) {
    return (
      <span className={`sync t-metaMono sync-${tone}`} title={label}>
        <span className="sync-dot" /> {label} · <a href={tellUsHref(queue.dropped)} target="_blank" rel="noopener">
          Tell us
        </a>
      </span>
    );
  }

  return (
    <span className={`sync t-metaMono sync-${tone}`} title={label} aria-label={label}>
      <span className="sync-dot" />
      {asks && ` ${label}`}
    </span>
  );
}

export function tellUsHref(drop: DroppedBatch, bundle: string = __BUNDLE__): string {
  const subject =
    drop.cause === 'newer-app'
      ? `Changes dropped for a newer app's file (${drop.fileCount} files)`
      : `Too many changes at once (${drop.fileCount} files)`;
  // RFC 6068: a line break in a mailto body is an encoded CRLF.
  const body = [`Files: ${drop.fileCount}`, `Bundle: ${bundle}`, `Time: ${new Date(drop.atMs).toISOString()}`].join('\r\n');
  return `mailto:feedback@pondering.app?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
